import React, { useState, useRef } from 'react';
import axios from 'axios';
import { json2csv } from 'json-2-csv';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import './RealEstateAnalysis.css';

const beautifulCsvOptions = {
    delimiter: { field: ";" },             // красивее смотрится в Excel
    wrap: '"',                             // аккуратные значения
    trimFieldValues: true,                 // убирает мусорные пробелы
    trimHeaderFields: true,

    sortHeader: true,                      // аккуратный отсортированный header
    escapeHeaderNestedDots: false,         // делает заголовки чище
    expandNestedObjects: false,            // не раздувает CSV длинными ключами

    unwindArrays: false,                   // массивы не превращаются в кучу строк
    expandArrayObjects: false,             // и не расплющиваются в ключи

    excelBOM: true,                        // Excel корректно открывает UTF-8

    emptyFieldValue: "",                   // нет в CSV "undefined" / "null"

    useDateIso8601Format: true,            // аккуратные ISO-даты
    wrapBooleans: true,                    // Excel не портит TRUE/FALSE

    preventCsvInjection: true,             // защита от формул в Excel

    fieldTitleMap: {                       // можно расширять под свои нужды
        "Название": "Объект",
        "Тип объекта": "Тип",
        "Площадь (м²)": "Площадь",
        "Адрес": "Адрес",
        "Город": "Город",
        "Метро": "Метро",
        "Этаж": "Этаж",
        "Этажность": "Этажей",
        "Широта": "Latitude",
        "Долгота": "Longitude",
        "Ссылка": "URL",
        "Источник": "Портал",
        "Дата": "Дата публикации"
    }
};

const API_URL = 'https://admin.bottax.tech/api/object';

function RealEstateAnalysis() {
    const [rows, setRows] = useState([
        { id: 1, description: '', loading: false, results: null, error: null }
    ]);
    const [analyzingAll, setAnalyzingAll] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const fileInputRef = useRef(null);

    const handleDescriptionChange = (rowId, value) => {
        setRows(prevRows =>
            prevRows.map(row =>
                row.id === rowId ? { ...row, description: value } : row
            )
        );
    };

    const handleAnalyze = async (rowId) => {
        console.log(`Starting analysis for row ${rowId}`);

        // Берём снапшот строки из текущего state
        const targetRow = rows.find(r => r.id === rowId);
        if (!targetRow || !targetRow.description.trim()) {
            console.log(`Row ${rowId} has no description`);
            setRows(prevRows =>
                prevRows.map(r =>
                    r.id === rowId
                        ? { ...r, error: 'Введите описание объекта' }
                        : r
                )
            );
            return;
        }

        const description = targetRow.description.trim();
        console.log(`Row ${rowId} description: ${description.substring(0, 50)}...`);

        // Ставим loading
        setRows(prevRows =>
            prevRows.map(r =>
                r.id === rowId
                    ? { ...r, loading: true, error: null, results: null }
                    : r
            )
        );

        try {
            console.log(`Row ${rowId} - sending API request`);
            const response = await axios.post(`${API_URL}/analyze`, {
                description
            });

            console.log(`Row ${rowId} - API response received`, response.data?.success);

            if (response.data?.success) {
                const data = response.data.data;
                setRows(prevRows =>
                    prevRows.map(r =>
                        r.id === rowId
                            ? { ...r, loading: false, results: data, error: null }
                            : r
                    )
                );
            } else {
                setRows(prevRows =>
                    prevRows.map(r =>
                        r.id === rowId
                            ? {
                                ...r,
                                loading: false,
                                error: response.data?.error || 'Ошибка анализа'
                            }
                            : r
                    )
                );
            }
        } catch (err) {
            console.error(`Row ${rowId} - API error:`, err.message);
            setRows(prevRows =>
                prevRows.map(r =>
                    r.id === rowId
                        ? {
                            ...r,
                            loading: false,
                            error:
                                err.response?.data?.error ||
                                err.message ||
                                'Произошла ошибка'
                        }
                        : r
                )
            );
        }

        console.log(`Row ${rowId} - analysis complete`);
    };

    const handleAddRow = () => {
        setRows(prevRows => {
            const newId = prevRows.length
                ? Math.max(...prevRows.map(r => r.id)) + 1
                : 1;
            return [
                ...prevRows,
                {
                    id: newId,
                    description: '',
                    loading: false,
                    results: null,
                    error: null
                }
            ];
        });
    };

    const handleDeleteRow = (rowId) => {
        setRows(prevRows => {
            if (prevRows.length > 1) {
                return prevRows.filter(r => r.id !== rowId);
            }
            return prevRows;
        });
    };

    const handleExportCSV = async (rowId) => {
        const row = rows.find(r => r.id === rowId);
        if (!row || !row.results || !row.results.analogs || row.results.analogs.length === 0) {
            alert('Нет данных для экспорта');
            return;
        }

        try {
            const csvData = row.results.analogs.map((analog, index) => ({
                'Номер': index + 1,
                'Название': analog.title,
                'Площадь (м²)': analog.area_m2,
                'Тип объекта': analog.object_type || '',
                'Адрес': analog.address,
                'Город': analog.city || '',
                'Метро': analog.metro || '',
                'Этаж': analog.floor || '',
                'Этажность': analog.floors_total || '',
                'Широта': analog.lat || '',
                'Долгота': analog.lng || '',
                'Ссылка': analog.url,
                'Источник': analog.source,
                'Дата': analog.date || ''
            }));

            const csv = await json2csv(csvData, beautifulCsvOptions);

            const blob = new Blob(['\uFEFF' + csv], {
                type: 'text/csv;charset=utf-8;'
            });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            const citySafe = row.results.extracted?.city || 'city';
            const areaSafe = row.results.extracted?.area_m2 || 'area';
            const fileName = `analogs_${citySafe}_${areaSafe}m2_${new Date()
                .toISOString()
                .split('T')[0]}.csv`;

            link.href = url;
            link.download = fileName;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Ошибка при экспорте CSV');
        }
    };

    const handleExportAllCSV = async () => {
        const allAnalogs = [];

        rows.forEach((row, rowIndex) => {
            if (row.results && row.results.analogs && row.results.analogs.length > 0) {
                const baseArea = row.results.extracted?.area_m2 || 0;
                row.results.analogs.forEach((analog, analogIndex) => {
                    allAnalogs.push({
                        'Объект №': rowIndex + 1,
                        'Описание объекта': row.description
                            ? row.description.substring(0, 100)
                            : '',
                        'Город объекта': row.results.extracted?.city || '',
                        'Площадь объекта (м²)': baseArea || '',
                        'Аналог №': analogIndex + 1,
                        'Название': analog.title,
                        'Площадь (м²)': analog.area_m2,
                        'Разница площади (м²)': Math.abs(
                            (analog.area_m2 || 0) - (baseArea || 0)
                        ),
                        'Тип объекта': analog.object_type || '',
                        'Адрес': analog.address,
                        'Город': analog.city || '',
                        'Метро': analog.metro || '',
                        'Этаж': analog.floor || '',
                        'Этажность': analog.floors_total || '',
                        'Широта': analog.lat || '',
                        'Долгота': analog.lng || '',
                        'Ссылка': analog.url,
                        'Источник': analog.source,
                        'Дата': analog.date || ''
                    });
                });
            }
        });

        if (allAnalogs.length === 0) {
            alert('Нет данных для экспорта');
            return;
        }

        try {
            const csv = await json2csv(allAnalogs, beautifulCsvOptions);
            const blob = new Blob(['\uFEFF' + csv], {
                type: 'text/csv;charset=utf-8;'
            });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            const fileName = `all_analogs_${new Date()
                .toISOString()
                .split('T')[0]}.csv`;

            link.href = url;
            link.download = fileName;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            alert('Ошибка при экспорте CSV');
        }
    };

    const handleImportFile = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (fileExtension === 'csv') {
            Papa.parse(file, {
                header: true,
                encoding: 'UTF-8',
                complete: (results) => {
                    processImportedData(results.data);
                },
                error: (error) => {
                    console.error('CSV parse error:', error);
                    alert('Ошибка при чтении CSV файла');
                }
            });
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

                    const headers = jsonData[0];
                    const dataRows = jsonData.slice(1).map(row => {
                        const obj = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index];
                        });
                        return obj;
                    });

                    processImportedData(dataRows);
                } catch (error) {
                    console.error('Excel parse error:', error);
                    alert('Ошибка при чтении Excel файла');
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert('Поддерживаются только файлы CSV, XLS и XLSX');
        }

        event.target.value = '';
    };

    const processImportedData = (data) => {
        if (!data || data.length === 0) {
            alert('Файл пустой или не содержит данных');
            return;
        }

        const possibleColumns = [
            'описание', 'description', 'объект', 'object', 'адрес', 'address',
            'текст', 'text', 'данные', 'data', 'недвижимость', 'property'
        ];

        const firstRow = data[0];
        const columnNames = Object.keys(firstRow || {});
        let descriptionColumn = null;

        for (const col of columnNames) {
            const colLower = String(col).toLowerCase();
            if (possibleColumns.some(possible => colLower.includes(possible))) {
                descriptionColumn = col;
                break;
            }
        }

        if (!descriptionColumn && columnNames.length > 0) {
            descriptionColumn = columnNames[0];
        }

        if (!descriptionColumn) {
            alert('Не удалось определить колонку с описаниями');
            return;
        }

        const descriptions = data
            .map(row => row[descriptionColumn])
            .filter(desc => desc && desc.toString().trim().length > 0);

        if (descriptions.length === 0) {
            alert('Не найдено описаний в файле');
            return;
        }

        const newRows = descriptions.map((desc, index) => ({
            id: Date.now() + index,
            description: desc.toString().trim(),
            loading: false,
            results: null,
            error: null
        }));

        setRows(newRows);
        alert(`Загружено ${descriptions.length} объектов для анализа`);
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleAnalyzeAll = async () => {
        const unanalyzedIds = rows
            .filter(r => !r.results && !r.loading && r.description.trim())
            .map(r => r.id);

        if (unanalyzedIds.length === 0) {
            alert('Нет объектов для анализа');
            return;
        }

        const confirmed = window.confirm(
            `Вы хотите проанализировать ${unanalyzedIds.length} объектов?\n\n` +
            `Это займет примерно ${Math.ceil(unanalyzedIds.length * 6)} секунд ` +
            `(~6 сек на объект из-за rate limit API).\n\n` +
            `Продолжить?`
        );

        if (!confirmed) return;

        console.log('Starting batch analysis for IDs:', unanalyzedIds);
        setAnalyzingAll(true);
        setProgress({ current: 0, total: unanalyzedIds.length });

        for (let i = 0; i < unanalyzedIds.length; i++) {
            const rowId = unanalyzedIds[i];
            console.log(`\n=== Analyzing ${i + 1}/${unanalyzedIds.length}: Row ID ${rowId} ===`);
            setProgress({ current: i + 1, total: unanalyzedIds.length });

            await handleAnalyze(rowId);
            console.log(`=== Completed ${i + 1}/${unanalyzedIds.length} ===\n`);

            if (i < unanalyzedIds.length - 1) {
                console.log('Waiting 6 seconds before next request...');
                await new Promise(resolve => setTimeout(resolve, 6000));
            }
        }

        console.log('Batch analysis complete!');
        setAnalyzingAll(false);
        setProgress({ current: 0, total: 0 });
        alert('Анализ всех объектов завершен!');
    };

    return (
        <div className="real-estate-container">
            <div className="page-header">
                <h1>Анализ аналогов недвижимости</h1>
                {analyzingAll && (
                    <div className="progress-banner">
                        <div className="progress-text">
                            Массовый анализ: {progress.current} из {progress.total} объектов
                        </div>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{
                                    width: progress.total
                                        ? `${(progress.current / progress.total) * 100}%`
                                        : '0%'
                                }}
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            <div className="table-wrapper">
                <table className="analogs-table">
                    <thead>
                        <tr>
                            <th className="col-object">Ваш объект</th>
                            <th className="col-analog">Аналог 1</th>
                            <th className="col-analog">Аналог 2</th>
                            <th className="col-analog">Аналог 3</th>
                            <th className="col-price">Средняя цена/м²</th>
                            <th className="col-value">Оценочная стоимость</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const topAnalogs = row.results?.analogs?.slice(0, 3) || [];

                            return (
                                <tr key={row.id} className="main-row">
                                    <td className="object-cell">
                                        <div className="object-input">
                                            <textarea
                                                value={row.description}
                                                onChange={(e) =>
                                                    handleDescriptionChange(row.id, e.target.value)
                                                }
                                                placeholder="Например: Warehouse in Moscow, 1000 m², built in 2005"
                                                disabled={row.loading}
                                                rows={4}
                                            />
                                            <div className="button-group">
                                                <button
                                                    className="btn-analyze"
                                                    onClick={() => handleAnalyze(row.id)}
                                                    disabled={row.loading}
                                                >
                                                    {row.loading ? 'Анализ...' : 'Анализировать'}
                                                </button>
                                                {rows.length > 1 && (
                                                    <button
                                                        className="btn-delete"
                                                        onClick={() => handleDeleteRow(row.id)}
                                                        disabled={row.loading}
                                                        title="Удалить строку"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {row.loading && (
                                            <div className="row-loading">
                                                <div className="mini-spinner"></div>
                                                <span>Поиск аналогов...</span>
                                            </div>
                                        )}

                                        {row.error && (
                                            <div className="row-error">
                                                {row.error}
                                            </div>
                                        )}

                                        {row.results?.extracted && (
                                            <div className="extracted-info">
                                                <div className="info-row">
                                                    <span className="label">Город:</span>
                                                    <span className="value">
                                                        {row.results.extracted.city}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="label">Площадь:</span>
                                                    <span className="value">
                                                        {row.results.extracted.area_m2} м²
                                                    </span>
                                                </div>
                                                {row.results.extracted.year_built && (
                                                    <div className="info-row">
                                                        <span className="label">Год:</span>
                                                        <span className="value">
                                                            {row.results.extracted.year_built}
                                                        </span>
                                                    </div>
                                                )}
                                                {row.results.analogs_count > 0 && (
                                                    <div className="info-row">
                                                        <span className="label">Найдено:</span>
                                                        <span className="value">
                                                            {row.results.analogs_count} аналогов
                                                        </span>
                                                    </div>
                                                )}
                                                {row.results.analogs_count > 0 && (
                                                    <button
                                                        className="btn-export-csv"
                                                        onClick={() => handleExportCSV(row.id)}
                                                        title="Экспортировать все аналоги в CSV"
                                                    >
                                                        Скачать CSV ({row.results.analogs_count})
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>

                                    {[0, 1, 2].map((idx) => {
                                        const analog = topAnalogs[idx];
                                        return (
                                            <td key={idx} className="analog-cell">
                                                {analog ? (
                                                    <>
                                                        <div className="analog-title">
                                                            {analog.title}
                                                        </div>
                                                        <div className="analog-details">
                                                            <div className="detail-row">
                                                                <span>
                                                                    Площадь: {analog.area_m2} м²
                                                                </span>
                                                            </div>
                                                            {analog.object_type && (
                                                                <div className="detail-row">
                                                                    <span>{analog.object_type}</span>
                                                                </div>
                                                            )}
                                                            {analog.address && (
                                                                <div className="detail-row">
                                                                    <span>{analog.address}</span>
                                                                </div>
                                                            )}
                                                            {analog.metro && (
                                                                <div className="detail-row">
                                                                    <span>{analog.metro}</span>
                                                                </div>
                                                            )}
                                                            {(analog.floor || analog.floors_total) && (
                                                                <div className="detail-row">
                                                                    <span>
                                                                        Этаж: {analog.floor || '-'}
                                                                        {analog.floors_total
                                                                            ? `/${analog.floors_total}`
                                                                            : ''}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {analog.url && (
                                                                <div className="detail-row">
                                                                    <a
                                                                        href={analog.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="analog-link"
                                                                    >
                                                                        Смотреть →
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="empty-analog">
                                                        {row.results
                                                            ? 'Нет данных'
                                                            : row.loading
                                                                ? 'Загрузка...'
                                                                : '—'}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}

                                    <td className="price-cell">
                                        <div className="empty-value">Н/Д</div>
                                    </td>

                                    <td className="value-cell">
                                        <div className="empty-value">Н/Д</div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="footer-actions">
                <button className="btn-add-row" onClick={handleAddRow}>
                    + Добавить строку
                </button>
                <button className="btn-import" onClick={handleImportClick}>
                    Импорт из Excel/CSV
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleImportFile}
                    style={{ display: 'none' }}
                />
                {rows.length > 1 && rows.some(r => !r.results && r.description.trim()) && (
                    <button
                        className="btn-analyze-all"
                        onClick={handleAnalyzeAll}
                        disabled={analyzingAll}
                    >
                        {analyzingAll
                            ? `Анализ... (${progress.current}/${progress.total})`
                            : `Анализировать все (${rows.filter(r => !r.results && r.description.trim()).length})`
                        }
                    </button>
                )}
                {rows.some(r => r.results?.analogs?.length > 0) && (
                    <button className="btn-export-all" onClick={handleExportAllCSV}>
                        Экспортировать все в CSV
                    </button>
                )}
                <div className="info-text">
                    Всего объектов: {rows.length}
                </div>
            </div>
        </div>
    );
}

export default RealEstateAnalysis;
